import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';
import {
  RecentActivityResponse,
  RecentActivityRequest,
  Activity,
  ActivityStats,
  AvailableFeedRequest,
  AvailableFeedResponse,
} from 'types/interfaces/activity';

export const activityApi = createApi({
  reducerPath: 'activityApi',
  baseQuery: baseQuery,
  tagTypes: ['RecentActivity', 'AvailableFeed'],
  endpoints: (builder) => ({
    getRecentActivity: builder.query<RecentActivityResponse, RecentActivityRequest>({
      query: ({ limit = 54, cursor } = {}) => {
        const params = new URLSearchParams();
        params.append('limit', String(limit));
        if (cursor) {
          params.append('cursor', cursor);
        }
        return {
          url: `/activity/recent?${params.toString()}`,
          method: 'GET',
          requiresAuth: false, // Public endpoint for social proof
        };
      },
      providesTags: ['RecentActivity'],
      keepUnusedDataFor: 60, // Cache for 60 seconds
    }),

    getActivityFeed: builder.query<RecentActivityResponse, RecentActivityRequest>({
      query: ({ limit = 30, cursor } = {}) => {
        const params = new URLSearchParams();
        params.append('limit', String(limit));
        if (cursor) {
          params.append('cursor', cursor);
        }
        return {
          url: `/activity/recent?${params.toString()}`,
          method: 'GET',
          requiresAuth: false,
        };
      },
      // Collapse all paginated calls into a single cache entry
      serializeQueryArgs: ({ endpointName }) => endpointName,
      // Merge paginated results into existing cache
      merge: (currentCache, newItems, { arg }) => {
        if (!arg.cursor) {
          // Fresh fetch (no cursor) = replace entire cache
          return newItems;
        }
        // Paginated fetch = append new items, deduplicate by ID
        const existingIds = new Set(currentCache.activities.map(a => a.id));
        const uniqueNewItems = newItems.activities.filter(a => !existingIds.has(a.id));
        currentCache.activities.push(...uniqueNewItems);
        currentCache.nextCursor = newItems.nextCursor;
        currentCache.hasMore = newItems.hasMore;
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.cursor !== previousArg?.cursor;
      },
      keepUnusedDataFor: 120,
    }),

    getAvailableFeed: builder.query<AvailableFeedResponse, AvailableFeedRequest>({
      query: ({ limit = 20, hours = 24 } = {}) => {
        const params = new URLSearchParams();
        params.append('limit', String(limit));
        params.append('hours', String(hours));
        return {
          url: `/activity/available?${params.toString()}`,
          method: 'GET',
          requiresAuth: true,
        };
      },
      transformResponse: (response: any): AvailableFeedResponse => {
        // Handle various backend response shapes
        const items = response?.items ?? response?.activities ?? [];
        return {
          items: Array.isArray(items) ? items : [],
          totalItems: response?.totalItems ?? items.length ?? 0,
          lookbackHours: response?.lookbackHours ?? 24,
        };
      },
      providesTags: ['AvailableFeed'],
      keepUnusedDataFor: 120,
    }),

    getActivityStats: builder.query<ActivityStats, void>({
      query: () => ({
        url: '/activity/stats',
        method: 'GET',
        requiresAuth: false,
      }),
      keepUnusedDataFor: 30,
    }),
  }),
});

export const {
  useGetRecentActivityQuery,
  useGetActivityFeedQuery,
  useGetAvailableFeedQuery,
  useGetActivityStatsQuery,
} = activityApi;

/**
 * Helper to prepend a new activity to the RTK Query cache
 * Used by WebSocket handler to update cache optimistically
 */
export const prependActivityToCache = (
  dispatch: any,
  newActivity: Activity,
  queryArgs: RecentActivityRequest = { limit: 54 }
) => {
  dispatch(
    activityApi.util.updateQueryData('getRecentActivity', queryArgs, (draft) => {
      // Prepend new activity
      draft.activities.unshift(newActivity);
      // Maintain max 54 activities (9 rows × 6 cards)
      if (draft.activities.length > 54) {
        draft.activities.pop();
      }
    })
  );
};

/**
 * Helper to prepend a new activity to the activity feed cache
 * Used by WebSocket handler - safe no-op when feed cache doesn't exist
 */
export const prependActivityToFeedCache = (
  dispatch: any,
  newActivity: Activity,
) => {
  dispatch(
    activityApi.util.updateQueryData(
      'getActivityFeed',
      // Must match serializeQueryArgs key (endpointName)
      undefined as unknown as RecentActivityRequest,
      (draft) => {
        if (!draft.activities.some(a => a.id === newActivity.id)) {
          draft.activities.unshift(newActivity);
        }
      }
    )
  );
};

/**
 * Helper to update activity stats cache from WebSocket event
 */
export const updateActivityStatsCache = (
  dispatch: any,
  stats: ActivityStats,
) => {
  dispatch(
    activityApi.util.updateQueryData('getActivityStats', undefined, (draft) => {
      draft.tradesLastHour = stats.tradesLastHour;
      draft.packagesShippedToday = stats.packagesShippedToday;
      draft.cardsTradedThisWeek = stats.cardsTradedThisWeek;
    })
  );
};
